import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { joinProviders, leaveProviders } from '@/lib/ws/socket';
import {
  fetchAirlineProvider,
  fetchFlightData,
  fetchIntegrations,
  fetchProvidersStatus,
  fetchRouting,
  probeIntegration,
  refreshFlightData,
  saveRouting,
  setIntegrationMode,
  switchAirlineProvider,
  toggleIntegration,
} from './api';
import { AIRLINE_PROVIDER_VENDOR, airlineRows, pendingKey } from './airline-provider';
import { AIRLINE_PROVIDER_QUERY_KEY, INTEGRATIONS_QUERY_KEY, ROUTING_QUERY_KEY } from './integration-changed';
import { sameBinding } from './integration-state';
import { RoutingSaveError } from './provider-routing';
import type {
  AirlineProviderState,
  FlightDataStatus,
  IntegrationDescriptor,
  IntegrationMode,
  ProvidersIntegrations,
  ProvidersRouting,
  ProvidersStatus,
  RoutingSaveBody,
  RoutingView,
} from './types';

/** §14.5 — the cache key of the "Orden de uso" views (defined beside the WS merge). */
export { ROUTING_QUERY_KEY };

/**
 * Live provider health for the dedicated page (D). ONE HTTP read on mount to seed
 * the cache, then defers to the WS push (`ProviderStatusChanged` → setQueryData
 * via applyEvent) — no `refetchInterval`, per frontend-conventions §15. Joins the
 * global `providers` WS room while mounted so the BFF runs its consolidating
 * refresh only when the page is open.
 */
export function useProvidersStatus(enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    joinProviders();
    return () => leaveProviders();
  }, [enabled]);

  return useQuery<ProvidersStatus>({
    queryKey: ['providers', 'status'],
    queryFn: fetchProvidersStatus,
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** Flight-data (FlightAware/AeroAPI) card state. Plain query — the freshness
 *  only moves when a pull runs, so a short staleTime + the explicit
 *  invalidation after a forced refresh is enough (no WS room for this one). */
export function useFlightData() {
  return useQuery<FlightDataStatus>({
    queryKey: ['providers', 'flight-data'],
    queryFn: fetchFlightData,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/** Operator-forced AeroAPI re-pull. On success re-reads the card and drops the
 *  cached flight lists so every board re-browses the fresh schedule. */
export function useRefreshFlightData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: refreshFlightData,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['providers', 'flight-data'] });
      void qc.invalidateQueries({ queryKey: ['flights'] });
    },
  });
}

/** How long after a read with rows still `checking` the screen reads again. */
export const INTEGRATIONS_FOLLOW_UP_MS = 5_000;
/** At most this many follow-up reads per mount — a bounded catch-up, never a poll. */
export const INTEGRATIONS_MAX_FOLLOW_UPS = 2;

/**
 * What this instance is connected to, and whether each integration is
 * available (slice E2 + #840).
 *
 * ONE read on mount. The operator-bff computes every row's `health` on that
 * read and answers within a few seconds; a probe that has not finished by then
 * comes back as `checking` and lands in the BFF's cache moments later. Only in
 * that case the screen reads AGAIN — after {@link INTEGRATIONS_FOLLOW_UP_MS},
 * at most {@link INTEGRATIONS_MAX_FOLLOW_UPS} times — to pick it up. That is a
 * bounded catch-up on one answer, not a feed: once nothing is `checking` (or
 * the cap is reached) the section never reads again on its own, honouring
 * frontend-conventions §8.1 (no polling for live state).
 *
 * M2: what OTHER operators change (switch, mode, order) arrives over the WS —
 * `IntegrationChanged` in the `providers` room re-reads this query once
 * (`lib/providers/integration-changed.ts`). The page joins that room via
 * {@link useProvidersStatus}.
 */
export function useIntegrations() {
  const query = useQuery<ProvidersIntegrations>({
    queryKey: ['providers', 'integrations'],
    queryFn: () => fetchIntegrations(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const checking = query.data?.integrations.some((i) => i.health?.state === 'checking') ?? false;
  const followUps = useRef(0);
  const { refetch, dataUpdatedAt } = query;
  useEffect(() => {
    if (!checking || followUps.current >= INTEGRATIONS_MAX_FOLLOW_UPS) return;
    const timer = setTimeout(() => {
      followUps.current += 1;
      void refetch();
    }, INTEGRATIONS_FOLLOW_UP_MS);
    return () => clearTimeout(timer);
  }, [checking, dataUpdatedAt, refetch]);

  return query;
}

/**
 * When the inventory on screen was computed, for the page header. A PASSIVE
 * reader of the same cache entry as {@link useIntegrations}: it never fetches
 * and runs no catch-up of its own, so mounting it beside the section cannot
 * double the bounded follow-up reads. Same options, so whichever observer
 * registers last cannot change how the shared query retries.
 */
export function useIntegrationsComputedAt(): string | undefined {
  return useQuery<ProvidersIntegrations, Error, string>({
    queryKey: ['providers', 'integrations'],
    queryFn: () => fetchIntegrations(),
    enabled: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
    select: (inventory) => inventory.computedAt,
  }).data;
}

/**
 * The operator-triggered connection test for one vendor — the MANUAL re-check
 * of the automatic health. A MUTATION, not a feed: it spends a real vendor
 * call, so it runs when — and only when — the operator presses the button. One
 * instance per card, so each integration keeps its own last result.
 */
export function useIntegrationProbe(vendor: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => probeIntegration(vendor),
    // #840: the BFF stored this verdict in its health cache; one re-read brings
    // the summary counts in line with the row, which already shows the result.
    onSuccess: (outcome) => {
      if (outcome.kind === 'result') {
        void qc.invalidateQueries({ queryKey: ['providers', 'integrations'] });
      }
    },
  });
}

/**
 * #839 — the operator switch for one row. NOT optimistic: the cached inventory
 * changes only when the server answers, and then with the row exactly as it was
 * committed (effective `enabled`, the switch's new `allowed`/`reason`, who and
 * why). The other rows are re-read too, because flipping one can change what a
 * sibling may do (switching ETG on frees the mock's "last provider" lock).
 */
export function useIntegrationToggle(descriptor: Pick<IntegrationDescriptor, 'vendor' | 'category'>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ enabled, reason }: { enabled: boolean; reason: string }) =>
      toggleIntegration(descriptor, enabled, reason),
    onSuccess: (row) => {
      commitRow(qc, row);
      void qc.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
      // §14.5: a vendor switched on/off changes what each routing SERVES now.
      void qc.invalidateQueries({ queryKey: ROUTING_QUERY_KEY });
      // The airline provider's card says what each of its services gives Nexa.
      if (row.vendor === AIRLINE_PROVIDER_VENDOR) void qc.invalidateQueries({ queryKey: AIRLINE_PROVIDER_QUERY_KEY });
    },
  });
}

/** Put the row the server committed in place of the cached one (same vendor × service). */
function commitRow(qc: ReturnType<typeof useQueryClient>, row: IntegrationDescriptor): void {
  qc.setQueryData<ProvidersIntegrations>(INTEGRATIONS_QUERY_KEY, (prev) =>
    prev ? { ...prev, integrations: prev.integrations.map((d) => (sameBinding(d, row) ? row : d)) } : prev,
  );
}

/** How long after a read that still disagrees with the switch the airline card reads again. */
export const AIRLINE_FOLLOW_UP_MS = 5_000;
/** At most this many follow-ups per disagreement — the airline's switch cache is seconds, not minutes. */
export const AIRLINE_MAX_FOLLOW_UPS = 4;

/**
 * The airline provider's card (`airline-air-ticketing.v1` §0). ONE read on mount; re-read when a
 * switch of it lands (here or, over the WS, anywhere).
 *
 * `integrations` — the inventory on screen (the operator's switch per service). When the airline
 * still reads a switch the control plane already moved (its resolve cache, seconds), and only
 * then, the hook reads again after {@link AIRLINE_FOLLOW_UP_MS}, at most
 * {@link AIRLINE_MAX_FOLLOW_UPS} times per disagreement: a bounded catch-up on one change, never
 * a poll (frontend-conventions §8.1). A new disagreement (another flip, of any service) gets its
 * own catch-up. A switch the airline cannot read (`null`) is not a disagreement that passes and
 * is never re-read for.
 *
 * `catchingUp` — a re-read is still coming (or running) for what disagrees now; `false` once the
 * catch-up is spent, so the card never promises a read that will not happen. `recheck` — one
 * read, when the operator asks for it.
 */
export function useAirlineProvider(integrations?: readonly IntegrationDescriptor[]) {
  const query = useQuery<AirlineProviderState>({
    queryKey: AIRLINE_PROVIDER_QUERY_KEY,
    queryFn: () => fetchAirlineProvider(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const disagreement = pendingKey(query.data, airlineRows(integrations));
  // The follow-ups spent on the disagreement on screen; a different one starts again from zero.
  const [spent, setSpent] = useState({ key: '', count: 0 });
  if (spent.key !== disagreement) setSpent({ key: disagreement, count: 0 });
  const count = spent.key === disagreement ? spent.count : 0;
  const { refetch, dataUpdatedAt } = query;
  useEffect(() => {
    if (!disagreement || count >= AIRLINE_MAX_FOLLOW_UPS) return;
    const timer = setTimeout(() => {
      setSpent({ key: disagreement, count: count + 1 });
      void refetch();
    }, AIRLINE_FOLLOW_UP_MS);
    return () => clearTimeout(timer);
  }, [disagreement, count, dataUpdatedAt, refetch]);

  const catchingUp = disagreement !== '' && (count < AIRLINE_MAX_FOLLOW_UPS || query.isFetching);
  return { query, catchingUp, recheck: () => void refetch() };
}

/**
 * The airline provider's MASTER switch — every service on, or every service off, with one
 * reason. NOT optimistic: each committed row replaces its cached one when the server answers;
 * then the inventory, the order of use and the airline card are re-read.
 */
export function useAirlineProviderSwitch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ enabled, reason }: { enabled: boolean; reason: string }) => switchAirlineProvider(enabled, reason),
    onSuccess: (result) => {
      for (const outcome of result.services) if (outcome.ok) commitRow(qc, outcome.row);
      void qc.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: ROUTING_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: AIRLINE_PROVIDER_QUERY_KEY });
    },
  });
}

/**
 * M2 (D14–D16) — choose the mode of one binding. NOT optimistic, exactly like
 * the switch: the row changes only when the server answers, and then with what
 * it committed (`effectiveMode`, `modeOverride` — possibly flagged `ignored`).
 * Then one re-read, because the BFF recomputes health on it (a synthetic row
 * turned real is checked for real).
 */
export function useIntegrationMode(descriptor: Pick<IntegrationDescriptor, 'vendor' | 'category'>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ mode, reason }: { mode: IntegrationMode; reason: string }) =>
      setIntegrationMode(descriptor, mode, reason),
    onSuccess: (row) => {
      commitRow(qc, row);
      void qc.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
    },
  });
}

/**
 * §14.5 — the "Orden de uso" of every routed key. ONE read on mount (no poll);
 * refreshed after a save, a conflict, or a switch flipped on the same page.
 */
export function useProviderRouting() {
  return useQuery<ProvidersRouting>({
    queryKey: ROUTING_QUERY_KEY,
    queryFn: () => fetchRouting(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * §14.5 — save one key. NOT optimistic: the cached view changes only when the
 * server answers, with the view exactly as committed; the inventory is re-read
 * too (what serves where). A 409 `version_conflict` re-reads the routing so the
 * panel can reload the decision that won.
 */
export function useSaveRouting(view: Pick<RoutingView, 'category' | 'capability'>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RoutingSaveBody) => saveRouting(view, body),
    onSuccess: (saved) => {
      qc.setQueryData<ProvidersRouting>(ROUTING_QUERY_KEY, (prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((v) =>
                v.category === saved.category && v.capability === saved.capability ? saved : v,
              ),
            }
          : prev,
      );
      void qc.invalidateQueries({ queryKey: ['providers', 'integrations'], exact: true });
    },
    onError: (err) => {
      if (err instanceof RoutingSaveError && err.code === 'version_conflict') {
        void qc.invalidateQueries({ queryKey: ROUTING_QUERY_KEY });
      }
    },
  });
}
